import React, { useMemo, useRef, useState } from "react"
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import axios from "axios"
import { useDispatch } from "react-redux"
import { openToastify } from "../../store/actions/toastifyAction"
import { REDUX_TOASTIFY_DEFAULTS } from "../../store/config"
import { purchseGiftcard } from "../../services/api/apiModule"
import { useNavigate } from "react-router-dom"
import moment from "moment/moment"

const presetAmounts = [50, 75, 100, 150, 200]
const GiftCard = () => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    phoneRaw: "",
    email: "",
    amountType: "preset", // "preset" | "custom"
    presetAmount: 100,
    customAmount: "",
    description: "",
    note: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isCardComplete, setIsCardComplete] = useState(false)
  const [purchaseGiftCard, setPurchasedGiftCard] = useState({
    giftCardCode: "",
  })
  const navigate = useNavigate()
  const cardRef = useRef(null)

  const baseAmount = useMemo(() => {
    if (formData.amountType === "preset") return formData.presetAmount || 0
    const val = parseFloat(formData.customAmount)
    return Number.isNaN(val) ? 0 : Math.max(val, 0)
  }, [formData])

  const tax = useMemo(() => baseAmount * 0.05, [baseAmount]) // 5%
  const total = useMemo(() => baseAmount + tax, [baseAmount, tax])

  const handleChange = (field) => (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      [field]: field === "presetAmount" ? Number(value) : value,
    }))
  }
  const handlePhoneChange = (e) => {
    const input = e.target.value
    let digits = input.replace(/\D/g, "")

    // strip country code if user types +1
    if (digits.startsWith("1")) {
      digits = digits.slice(1)
    }

    digits = digits.slice(0, 10)

    setFormData((prev) => ({
      ...prev,
      phone: formatPhoneUS(digits),
      phoneRaw: digits,
    }))
  }

  const handleAmountTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      amountType: type,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!baseAmount) {
      dispatch(
        openToastify(
          "Please select or enter a valid gift card value",
          REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_WARN()
        )
      )
      return
    }

    if (!stripe || !elements) {
      dispatch(
        openToastify(
          "Payment system is not ready yet. Please try again in a moment.",
          REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_ERROR()
        )
      )
      return
    }

    setIsLoading(true)

    const cardElement = elements.getElement(CardElement)
    const { error: paymentMethodError, paymentMethod } =
      await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      })

    if (paymentMethodError) {
      dispatch(
        openToastify(
          paymentMethodError.message,
          REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_ERROR()
        )
      )
      setIsLoading(false)
      return
    }

    try {
      const payload = {
        name: formData.name,
        phoneNumber: formData.phoneRaw,
        email: formData.email,
        ShortDescription: formData.description,
        note: formData.note,
        currency: "cad",
        paymentMethodId: paymentMethod.id,
        minOrderValue: 50,
        amount: baseAmount * 100, // Convert to cents
        discount: 0,
        tax: tax,
        totalAmount: total,
      }

      const response = await purchseGiftcard(payload)

      if (response.success) {
        dispatch(
          openToastify(
            response.message,
            REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_SUCCESS()
          )
        )
        // elements.getElement(CardElement).clear()
        setPurchasedGiftCard(response.data || {})
        // setFormData({})
      } else {
        dispatch(
          openToastify(
            response.message,
            REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_ERROR()
          )
        )
      }
    } catch (err) {
      dispatch(
        openToastify(
          "An error occurred: " + (err.response?.data?.message || err.message),
          REDUX_TOASTIFY_DEFAULTS.UPDATE_TOASTIFY_ERROR()
        )
      )
    }
    setIsLoading(false)
  }

  const isSubmitDisabled =
    isLoading ||
    !isCardComplete ||
    !formData.name.trim() ||
    !formData.email.trim() ||
    !formData.phoneRaw.trim() ||
    baseAmount <= 19

  const formatCurrency = (value) =>
    value.toLocaleString("en-US", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    })

  const formatPhoneUS = (value) => {
    // remove everything except numbers
    let digits = value.replace(/\D/g, "")

    // remove leading country code if user enters +1
    if (digits.startsWith("1")) {
      digits = digits.slice(1)
    }

    digits = digits.slice(0, 10)

    const part1 = digits.slice(0, 3)
    const part2 = digits.slice(3, 6)
    const part3 = digits.slice(6, 10)

    if (digits.length > 6) {
      return `+1 (${part1}) ${part2}-${part3}`
    } else if (digits.length > 3) {
      return `+1 (${part1}) ${part2}`
    } else if (digits.length > 0) {
      return `+1 (${part1}`
    }

    return ""
  }

  return (
    <div className="min-h -screen text-slate-900 flex items-start lg:items-center justify-center px-4 py-8 lg:py-16">
      {/* Abstract background shapes */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -right-10 h-52 w-52 rounded-full bg-gradient-to-br from-[#e9c46b] via-[#e3b74e] to-[#cc9d51] blur-3xl opacity-60" />
        <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-gradient-to-tr from-[#cc9d51] via-[#e9c46b] to-[#ac7134] blur-3xl opacity-50" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-yellow-200/70 to-transparent" />
      </div>

      <div className="relative w-full max-w-md lg:max-w-5xl">
        {/* Floating accent shape */}
        <div className="absolute -top-6 right-6 h-10 w-10 rounded-2xl bg-gradient-to-br from-[#e9c46b] to-[#ac7134] opacity-90 shadow-lg shadow-yellow-200/80 rotate-6" />
        <div className="absolute -bottom-4 left-10 h-8 w-8 rounded-3xl border border-yellow-200 bg-white/80 shadow-[0_15px_35px_rgba(15,23,42,0.12)] rotate-12" />

        <div className="relative overflow-hidden rounded-3xl border border-black bg-white/90 shadow-2xl shadow-yellow-light_medium/70 backdrop-blur-xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
                Digital Gift Card
              </p>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                Send a little magic ✨
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-slate-500">
                Fill out the details below and securely pay with Stripe.
              </p>
            </div>
            <div className="flex flex-col items-start sm:items-end">
              <span className="rounded-full border border-[#e3b74e] bg-[#e9c46b]/20 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#ac7134]">
                Secure Checkout
              </span>
              <span className="mt-1 text-[10px] text-slate-500">
                256-bit SSL • Powered by Stripe
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 2-column layout on desktop */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-10">
              {/* LEFT: form fields */}
              <div className="space-y-4">
                {/* Name */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Buyer Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleChange("name")}
                    placeholder="Your full name"
                    className="w-full rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#e3b74e] focus:ring-2 focus:ring-[#e9c46b]/40 transition"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Buyer Phone number
                  </label>
                  <input
                    type="tel"
                    // maxLength={12}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="+1 (555) 000-0000"
                    className="w-full rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#e3b74e] focus:ring-2 focus:ring-[#e9c46b]/40 transition"
                  />
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Buyer Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={handleChange("email")}
                    placeholder="you@example.com"
                    className="w-full rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#e3b74e] focus:ring-2 focus:ring-[#e9c46b]/40 transition"
                  />
                </div>

                {/* Short description (internal / subject) */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Description{" "}
                    <span className="text-[10px] text-slate-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={handleChange("description")}
                    placeholder="e.g. Birthday gift card for John"
                    className="w-full rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#e3b74e] focus:ring-2 focus:ring-[#e9c46b]/40 transition"
                  />
                </div>

                {/* Note / message on the gift card */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Note on gift card{" "}
                    <span className="text-[10px] text-slate-400">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={formData.note}
                    onChange={handleChange("note")}
                    placeholder="Write a note on for what booking, services this giftcard"
                    rows={3}
                    className="w-full rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#e3b74e] focus:ring-2 focus:ring-[#e9c46b]/40 transition resize-none"
                  />
                </div>
              </div>

              {/* RIGHT: summary + CTA */}
              <div className="mt-6 lg:mt-0 lg:flex lg:flex-col lg:justify-between space-y-4 lg:space-y-6">
                {/* Gift card amount */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-slate-700">
                      Gift card value
                    </label>
                    <span className="text-[11px] text-slate-500">
                      Select an amount or enter your own
                    </span>
                  </div>

                  {/* Amount type toggle */}
                  <div className="flex rounded-2xl bg-[#fffaf0] p-0.5 border border-yellow-dark text-[11px] font-medium">
                    <button
                      type="button"
                      onClick={() => handleAmountTypeChange("preset")}
                      className={`flex-1 rounded-2xl px-2.5 py-1.5 transition ${
                        formData.amountType === "preset"
                          ? "bg-yellow-medium_dark text-slate-900 shadow-inner shadow-yellow-100"
                          : "text-slate-500"
                      }`}
                    >
                      Preset
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAmountTypeChange("custom")}
                      className={`flex-1 rounded-2xl px-2.5 py-1.5 transition ${
                        formData.amountType === "custom"
                          ? "bg-yellow-medium_dark text-slate-900 shadow-inner shadow-yellow-100"
                          : "text-slate-500"
                      }`}
                    >
                      Custom
                    </button>
                  </div>

                  {/* Preset options */}
                  {formData.amountType === "preset" && (
                    <div className="mt-2 grid grid-cols-3 gap-2 md:grid-cols-4 ">
                      {presetAmounts.map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              presetAmount: amount,
                            }))
                          }
                          className={`rounded-2xl border px-2.5 py-2 text-xs sm:text-[13px] font-medium transition shadow-sm ${
                            formData.presetAmount === amount
                              ? "border-[#e3b74e] bg-[#e9c46b]/20 text-[#ac7134] shadow-yellow-200/80"
                              : "border-slate-300 bg-white/90 text-slate-700 hover:border-[#e3b74e]"
                          }`}
                        >
                          {formatCurrency(amount)}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Custom amount */}
                  {formData.amountType === "custom" && (
                    <div className="mt-2 flex items-center gap-2 rounded-2xl border border-slate-300 bg-white/90 px-3.5 py-2.5">
                      <span className="text-sm text-slate-500">$</span>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={formData.customAmount}
                        onChange={handleChange("customAmount")}
                        placeholder="Enter amount"
                        className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none"
                      />
                    </div>
                  )}
                </div>

                {/* Price summary */}
                <div className="rounded-2xl border border-yellow-medium_dark bg-[#fffaf0] px-4 py-3 text-xs sm:text-sm shadow-[0_20px_45px_rgba(15,23,42,0.08)]">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Gift card</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(baseAmount)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-slate-700">
                      Tax <span className="text-[11px]">(5%)</span>
                    </span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(tax)}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-yellow-200 pt-2 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[11px] uppercase tracking-wide text-slate-700">
                        Total
                      </span>
                      <span className="text-[11px] text-slate-700">
                        Charged via Stripe
                      </span>
                    </div>
                    <span className="text-lg lg:text-xl font-semibold text-slate-900">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      navigate(
                        `/view-gift-card/${purchaseGiftCard?.giftCardCode}`
                      )
                    }}
                    type="button"
                    disabled={!purchaseGiftCard?.giftCardCode}
                    className={`group relative flex items-center justify-center rounded-2xl px-2 py-1 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${
                      purchaseGiftCard?.giftCardCode
                        ? "bg-gradient-to-r from-[#e3b74e] via-[#e9c46b] to-[#ac7134] text-slate-900 shadow-lg shadow-yellow-200/80 hover:brightness-105 focus:ring-[#e9c46b]"
                        : "cursor-n ot-allowed bg-slate-200 text-slate-400"
                    }`}
                  >
                    View Purchased Gift Card
                  </button>
                  <button
                    onClick={() => {
                      cardRef.current?.scrollIntoView({ behavior: "smooth" })
                    }}
                    type="button"
                    className={`group relative flex items-center justify-center rounded-2xl px-2 py-1 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#fffdf7] bg-gradient-to-r from-[#e3b74e] via-[#e9c46b] to-[#ac7134] text-slate-900 shadow-lg shadow-yellow-200/80 hover:brightness-105 focus:ring-[#e9c46b]      
                      `}
                  >
                    View Gift Card ↓
                  </button>
                </div>

                <div className="rounded-2xl border border-yellow-dark bg-[#fffaf0] px-4 py-3 text-xs sm:text-sm shadow-yellow-medium_dark">
                  <CardElement
                    onChange={(event) => setIsCardComplete(event.complete)}
                  />
                </div>

                {/* Stripe CTA */}
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className={`group relative flex w-full items-center justify-center overflow-hidden rounded-2xl px-4 py-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#fffdf7] ${
                      isSubmitDisabled
                        ? "cursor-not-allowed bg-slate-200 text-slate-400"
                        : "bg-gradient-to-r from-[#e3b74e] via-[#e9c46b] to-[#ac7134] text-slate-900 shadow-lg shadow-yellow-200/80 hover:brightness-105 focus:ring-[#e9c46b]"
                    }`}
                  >
                    {isLoading ? (
                      <span>Processing...</span>
                    ) : (
                      <span className="relative flex items-center gap-2">
                        <span>Pay securely with Stripe</span>
                        {!isSubmitDisabled && (
                          <span className="text-xs opacity-80 group-hover:translate-x-0.5 transition-transform">
                            ➝
                          </span>
                        )}
                      </span>
                    )}
                  </button>

                  {/* <p className="text-[11px] text-center text-slate-500">
                    By continuing, you agree to our{" "}
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-slate-700"
                    >
                      Terms
                    </button>{" "}
                    &{" "}
                    <button
                      type="button"
                      className="underline decoration-dotted underline-offset-2 hover:text-slate-700"
                    >
                      Privacy Policy
                    </button>
                    .
                  </p> */}
                </div>
              </div>
            </div>
          </form>
        </div>
        <div className="flex-1 flex items-center justify-center flex- mt-8">
          <div
            ref={cardRef}
            className="relative w-full max-w-[620px] md:aspect-[16/9] rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#1d3557] via-[#264653] to-[#2a9d8f]"
          >
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-20 mix-blend-soft-light">
              <div className="h-full w-full bg-[radial-gradient(circle_at_0_0,rgba(255,255,255,0.12)_0,transparent_55%),radial-gradient(circle_at_100%_0,rgba(255,255,255,0.08)_0,transparent_55%)]" />
            </div>

            {/* Ribbon */}
            <div className="absolute -left-8 top-6 rotate-[-8deg]">
              <div className="bg-gradient-to-r from-[#e9c46b] via-[#f4a261] to-[#e76f51] px-10 py-1.5 rounded-full shadow-lg shadow-slate-900/60 border border-white/30">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900">
                  {formData.description || "Description"}
                </span>
              </div>
            </div>

            {/* Brand corner tag */}
            <div className="absolute top-3 right-4 rounded-full bg-white/10 border border-white/20 px-3 py-1 backdrop-blur-sm">
              <span className="text-[11px] font-medium text-slate-100">
                Boost Mobile Detailing
              </span>
            </div>

            {/* Content */}
            <div className="relative z-10 h-full p-6 sm:p-8 flex flex-col justify-between">
              {/* Top section */}
              <div className="flex flex-col gap-3 md:pt-4 pt-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    {/* <span className="text-[11px] uppercase tracking-[0.2em] text-slate-300/90">
                        To
                      </span>
                      <span className="text-lg sm:text-xl font-semibold text-slate-50 truncate">
                        {formData.recipientName}
                      </span> */}
                  </div>
                  <div className="text-right">
                    <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                      Value
                    </span>
                    <div className="text-xl sm:text-2xl font-bold text-[#f4a261]">
                      {formatCurrency(formData.currentBalance || 0)}
                    </div>
                  </div>
                </div>

                <div className="mt-1 flex items-center justify-between gap-4">
                  <div className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                      From
                    </span>
                    <span className="text-sm font-medium text-slate-50">
                      {formData.name || "Boost"}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-[11px] uppercase tracking-[0.17em] text-slate-300/80">
                      Expires
                    </span>
                    <span className="text-xs font-medium text-slate-100">
                      {moment().add(5, "years").format("YYYY-MM-DD")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Middle message */}
              <div className="mt-4 flex-1 flex items-center">
                <div className="rounded-2xl bg-black/20 border border-white/10 px-4 py-3 max-h-28 overflow-hidden">
                  <p className="text-xs sm:text-sm text-slate-100/90 line-clamp-3">
                    {formData.note || "Mnetion note"}
                  </p>
                </div>
              </div>

              {/* Bottom code + strip */}
              <div className="mt-4 flex flex-col gap-3">
                {/* Decorative strip */}
                <div className="h-8 w-full rounded-2xl bg-[repeating-linear-gradient(135deg,rgba(250,250,250,0.1)_0,rgba(250,250,250,0.1)_8px,transparent_8px,transparent_16px)] border border-white/15 flex items-center px-4 justify-between">
                  <span className="text-[10px] uppercase tracking-[0.21em] text-slate-200/80">
                    Non-Refundable • One-Time Use • Online Booking • Balance
                    Based
                  </span>
                </div>

                {/* Code */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.21em] text-slate-300/90">
                      Gift Card Code
                    </span>
                    <div className="mt-1 inline-flex items-center gap-2 rounded-xl bg-black/30 px-3 py-1.5 border border-white/15">
                      <span className="text-[13px] font-mono tracking-[0.25em] text-slate-50">
                        {formData.giftCardCode || "XXXXXXXXXXXXX"}
                      </span>
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end text-[10px] text-slate-300/80">
                    <span>Present this code at checkout</span>
                    <span>Valid for detailing services only</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GiftCard
